import * as Yup from 'yup';
import PropTypes from 'prop-types';
import React from 'react';
import { useBoolean } from 'usehooks-ts';
import { useFormik } from 'formik';
import { useLocation, Link, useHistory } from 'react-router-dom';
import Flatpickr from 'react-flatpickr';
import { Vietnamese } from 'flatpickr/dist/l10n/vn';
import { Col, Row, FormFeedback } from 'reactstrap';

import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import Card from 'components/App/Card';
import ModalPromotionSummarize from 'components/Modals/ModalPromotionSummarize';
import ModalSuccess from 'components/Modals/ModalSuccess';
import AppInputNumberValidation from 'components/App/InputNumberValidation';
import AppInputValidation from 'components/App/InputValidation';

import DATES_OF_WEEK from 'constants/datesOfWeek';
import INITIAL_VALUE from 'constants/initialValue';
import { LOCATIONS, LOGIN_TYPE, storageKey, themeColor } from 'constants/index';
import { PROMOTION_CATEGORY, PROMOTION_TYPE } from 'constants/promotion';
import cn from 'utils/classnames';
import { convertIsoDateToString } from 'utils';
import { createPromotionValidation } from 'utils/validate';

import { ReactComponent as RankPromotionIcon } from 'assets/images/rank-promotion.svg';
import { ReactComponent as ChevronDown } from 'assets/images/Chervon.svg';

import styles from './CreateRankPromotion.module.scss';
import RankPromotionCalendar from './RankPromotionCalendar';

// BREADCRUMB constant moved inside component dynamically

const formatStayDateLabel = (date, prefix) => {
  if (!date) return '';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const day = d.getDate();
  const month = d.getMonth() + 1;
  const year = d.getFullYear();
  return `${prefix}: ${day} tháng ${month} ${year}`;
};

const getValidDate = (val) => {
  if (!val) return null;
  const date = new Date(val);
  return Number.isNaN(date.getTime()) ? null : date;
};

const isSameOrAfterDay = (date, minDate) => {
  const d = getValidDate(date);
  const min = getValidDate(minDate);
  if (!d || !min) return true;

  return d.setHours(0, 0, 0, 0) >= min.setHours(0, 0, 0, 0);
};

const getRootFormValue = (context, fieldName) => (
  context.from?.[context.from.length - 1]?.value?.[fieldName]
);

const stayStartAtValidation = Yup.mixed()
  .test(
    'stayStartAt-required',
    'Ngày bắt đầu lưu trú không được để trống',
    (value) => !!value,
  )
  .test({
    name: 'stayStartAt-bookingStart',
    message:
      'Thời gian bắt đầu lưu trú phải lớn hoặc bằng thời gian khách có thể đặt ưu đãi',
    test(value, context) {
      const bookingStartAt = getRootFormValue(context, 'bookingStartAt');
      if (!value || !bookingStartAt) return true;

      return isSameOrAfterDay(value, bookingStartAt);
    },
  });

const stayEndAtValidation = Yup.mixed()
  .test(
    'stayEndAt-required',
    'Ngày kết thúc lưu trú không được để trống',
    (value) => !!value,
  )
  .test({
    name: 'stayEndAt-order',
    message: 'Thời gian kết thúc phải lớn hoặc bằng hơn thời gian bắt đầu',
    test(value, context) {
      const { startAt } = context.parent;
      if (!startAt || !value) return true;

      const start = new Date(startAt);
      const end = new Date(value);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return true;

      return end.setHours(0, 0, 0, 0) >= start.setHours(0, 0, 0, 0);
    },
  });

function StayDatesDatePicker({
  prefix,
  placeholder,
  value,
  minDate,
  onChange,
}) {
  const formatDate = React.useCallback(
    (date) => formatStayDateLabel(date, prefix),
    [prefix],
  );

  const flatpickrOptions = React.useMemo(
    () => ({
      locale: Vietnamese,
      altInput: true,
      altInputClass: styles.DatePickerAltInput,
      dateFormat: 'd/m/Y',
      formatDate,
      minDate,
    }),
    [formatDate, minDate],
  );

  return (
    <div className={styles.DatePickerField}>
      <Flatpickr
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        options={flatpickrOptions}
        className={styles.DatePickerInput}
      />
      <ChevronDown className={styles.DatePickerFieldIconRight} />
    </div>
  );
}

StayDatesDatePicker.propTypes = {
  prefix: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  onChange: PropTypes.func.isRequired,
};

StayDatesDatePicker.defaultProps = {
  value: '',
  minDate: 'today',
};

function CreateRankPromotion(props) {
  const {
    isLoadingCreatePromotion,

    roomOfPlaceList,
    placePriceTypes,
    promotionDetail,
    placePromotionList,

    actionGetPromotionDetail,
    actionGetListRoomsOfPlace,
    actionGetPlacePriceTypes,
    actionCreatePlacePromotion,
    actionClearDataPromotionDetail,
    actionGetPlacePromotionList,
  } = props;

  const history = useHistory();
  const location = useLocation();
  const placeId = new URLSearchParams(location.search).get('place');
  const promotionId = new URLSearchParams(location.search).get('promotion');

  const isExtranet = React.useMemo(() =>
    localStorage.getItem(storageKey.LOGIN_TYPE) === LOGIN_TYPE.EXTRANET,
  []);

  const breadcrumbList = React.useMemo(() => [
    {
      name: 'Chương trình khuyến mãi',
      isActive: false,
      link: '',
    },
    {
      name: 'Tạo chương trình khuyến mãi',
      isActive: false,
      link: placeId
        ? `${LOCATIONS.LOCATION_DETAIL.CREATE_PROMOTION}?place=${placeId}`
        : LOCATIONS.LOCATION_DETAIL.CREATE_PROMOTION,
    },
    {
      name: 'Ưu Đãi Thứ Hạng',
      isActive: true,
      link: '',
    },
  ], [placeId]);

  const {
    value: isOpenModal,
    setTrue: onOpenModal,
    setFalse: onCloseModal,
  } = useBoolean(false);

  const {
    value: isOpenSuccessModal,
    setTrue: onOpenSuccessModal,
    setFalse: onCloseSuccessModal,
  } = useBoolean(false);

  const validation = useFormik({
    initialValues: {
      rooms: [],
      isAllRooms: true,
      isAllPriceTypes: true,
      applyBookingDays: DATES_OF_WEEK,
      priceTypes: [],
      bookingStartAt: '',
      bookingEndAt: '',
      stayAt: [
        {
          _id: '1',
          startAt: '',
          endAt: '',
        },
      ],
      discountRate: 0,
      discountType: PROMOTION_TYPE.MEMBER_PROMOTION,
      name: '',
    },

    validationSchema: Yup.object({
      rooms: createPromotionValidation.rooms,
      priceTypes: createPromotionValidation.priceTypes,
      applyBookingDays: createPromotionValidation.applyBookingDays,
      discountRate: createPromotionValidation.discountRate,
      bookingStartAt: createPromotionValidation.bookingStartAt,
      bookingEndAt: Yup.date()
        .test(
          'bookingEndAt',
          'Ngày khách có thể đặt ưu đãi không được để trống',
          (value) => value,
        )
        .test({
          message:
            'Thời gian kết thúc phải lớn hoặc bằng hơn thời gian bắt đầu',
          test(value) {
            // eslint-disable-next-line react/no-this-in-sfc
            const { bookingStartAt } = this.parent;
            if (!bookingStartAt || !value) return true;

            const start = new Date(bookingStartAt);
            const end = new Date(value);

            if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return true;

            return end.setHours(0, 0, 0, 0) >= start.setHours(0, 0, 0, 0);
          },
        }),
      stayAt: Yup.array()
        .of(
          Yup.object({
            _id: Yup.string(),
            startAt: stayStartAtValidation,
            endAt: stayEndAtValidation,
          }),
        )
        .min(1),
      name: createPromotionValidation.name,
    }),

    onSubmit: () => {
      onOpenModal();
    },
  });

  const onCallback = React.useCallback(() => {
    onCloseModal();
    onOpenSuccessModal();
  }, [onCloseModal, onOpenSuccessModal]);

  const handleCloseSuccessModal = React.useCallback(() => {
    onCloseSuccessModal();
    history.push({
      pathname: LOCATIONS.LOCATION_DETAIL.PROMOTION,
      search: `?place=${placeId}&status=PENDING`,
    });
  }, [placeId, history, onCloseSuccessModal]);

  const buildPromotionPayload = React.useCallback(
    () => ({
      id: promotionId || undefined,
      place: placeId,
      name: validation.values.name,
      category: PROMOTION_CATEGORY.LIMITED,
      type: PROMOTION_TYPE.RANK,
      discount: Number(validation.values.discountRate),
      bookingDate: {
        from: convertIsoDateToString(
          validation.values.bookingStartAt,
          'YYYY-MM-DD',
        ),
        to: convertIsoDateToString(
          validation.values.bookingEndAt,
          'YYYY-MM-DD',
        ),
      },
      checkInDate: {
        from: convertIsoDateToString(
          validation.values.stayAt[0]?.startAt,
          'YYYY-MM-DD',
        ),
        to: convertIsoDateToString(
          validation.values.stayAt[0]?.endAt,
          'YYYY-MM-DD',
        ),
      },
    }),
    [placeId, promotionId, validation.values],
  );

  const onSubmitCreatePromotion = React.useCallback(() => {
    const promotion = buildPromotionPayload();

    actionCreatePlacePromotion({
      promotion,
      promotionId,
      callback: onCallback,
    });
  }, [
    buildPromotionPayload,
    actionCreatePlacePromotion,
    promotionId,
    onCallback,
  ]);

  React.useEffect(() => {
    if (placeId) {
      actionGetListRoomsOfPlace({ placeId });
      actionGetPlacePriceTypes({ placeId });
      actionGetPlacePromotionList({ placeId, type: 'RANK' });
    }
  }, [
    placeId,
    actionGetListRoomsOfPlace,
    actionGetPlacePriceTypes,
    actionGetPlacePromotionList,
  ]);

  React.useEffect(() => {
    if (promotionId && placeId) {
      actionGetPromotionDetail({ placeId, promotionId });
    }

    return () => {
      actionClearDataPromotionDetail();
    };
  }, [
    promotionId,
    placeId,
    actionGetPromotionDetail,
    actionClearDataPromotionDetail,
  ]);

  React.useEffect(() => {
    if (Object.keys(promotionDetail).length > 0) {
      validation.setValues((values) => ({
        ...values,
        ...promotionDetail,
        discountRate: promotionDetail.discount,
        applyBookingDays: promotionDetail.applyBookingDays
          ? DATES_OF_WEEK.map((item) => ({
            ...item,
            isChecked: promotionDetail.applyBookingDays.includes(item.value),
          }))
          : DATES_OF_WEEK,
        bookingStartAt: promotionDetail.bookingDate?.from
          ? new Date(promotionDetail.bookingDate.from)
          : '',
        bookingEndAt: promotionDetail.bookingDate?.to
          ? new Date(promotionDetail.bookingDate.to)
          : '',
        stayAt: [
          {
            _id: '1',
            startAt: promotionDetail.checkInDate?.from
              ? new Date(promotionDetail.checkInDate.from)
              : '',
            endAt: promotionDetail.checkInDate?.to
              ? new Date(promotionDetail.checkInDate.to)
              : '',
          },
        ],
      }));
    }

    return () => {
      validation.resetForm();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promotionDetail]);

  const stayStartMinDate = React.useMemo(() => {
    const bookingStart = getValidDate(validation.values.bookingStartAt);
    if (bookingStart) return bookingStart;
    return 'today';
  }, [validation.values.bookingStartAt]);

  const stayEndMinDate = React.useMemo(() => {
    const stayStart = getValidDate(validation.values.stayAt[0]?.startAt);
    if (stayStart) return stayStart;
    return stayStartMinDate;
  }, [validation.values.stayAt, stayStartMinDate]);

  React.useEffect(() => {
    const bookingStart = getValidDate(validation.values.bookingStartAt);
    if (!bookingStart) return;

    const bookingStartTime = bookingStart.setHours(0, 0, 0, 0);
    const currentStay = validation.values.stayAt[0];
    const stayStart = getValidDate(currentStay?.startAt);
    const stayEnd = getValidDate(currentStay?.endAt);

    let nextStartAt = currentStay?.startAt;
    let nextEndAt = currentStay?.endAt;
    let shouldUpdate = false;

    if (stayStart && stayStart.setHours(0, 0, 0, 0) < bookingStartTime) {
      nextStartAt = '';
      nextEndAt = '';
      shouldUpdate = true;
    } else if (stayEnd && stayEnd.setHours(0, 0, 0, 0) < bookingStartTime) {
      nextEndAt = '';
      shouldUpdate = true;
    }

    if (shouldUpdate) {
      validation.setFieldValue('stayAt', [{
        ...currentStay,
        startAt: nextStartAt,
        endAt: nextEndAt,
      }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validation.values.bookingStartAt]);

  const onChangeStayStartAt = React.useCallback(
    (date) => {
      validation.setFieldTouched('stayAt.0.startAt', true);
      validation.setFieldValue('stayAt.0.startAt', date[0]);
    },
    [validation],
  );

  const onChangeStayEndAt = React.useCallback(
    (date) => {
      validation.setFieldTouched('stayAt.0.endAt', true);
      validation.setFieldValue('stayAt.0.endAt', date[0]);
    },
    [validation],
  );

  const stayStartAtError = validation.errors.stayAt?.[0]?.startAt;
  const stayEndAtError = validation.errors.stayAt?.[0]?.endAt;
  const isStayStartAtTouched = validation.touched.stayAt?.[0]?.startAt;
  const isStayEndAtTouched = validation.touched.stayAt?.[0]?.endAt;
  const showStayStartAtError = stayStartAtError
    && (isStayStartAtTouched || validation.submitCount > 0);
  const showStayEndAtError = stayEndAtError
    && (isStayEndAtTouched || validation.submitCount > 0);

  return (
    <div className={cn('page-content', styles.Page)}>
      <Breadcrumb pageTitle="Tạo Ưu Đãi Thứ Hạng" breadcrumbList={breadcrumbList} />

      <div className={styles.Wrapper}>
        <Card className={styles.Section}>
          <div className={styles.SectionTop}>
            <RankPromotionIcon className={styles.Icon} />

            <div className={styles.SectionTopTitle}>
              <h3>Tạo Ưu Đãi Thứ Hạng</h3>
              <p>
                Tăng khả năng xuất hiện của chỗ nghỉ trong top 20 tại thị trường
                mục tiêu bằng ưu đãi cạnh tranh trong thời gian giới hạn.
              </p>
            </div>
          </div>
        </Card>

        <RankPromotionCalendar
          validation={validation}
          placePromotionList={placePromotionList}
        />

        <Card className={styles.Section}>
          <h3 className={styles.Title}>Thông tin chi tiết về khuyến mãi</h3>

          <div className={styles.FormField}>
            <p className={styles.FormFieldTitle}>Giảm giá</p>
            <Row>
              <Col xs={5}>
                <AppInputNumberValidation
                  name="discountRate"
                  keyName="discountRate"
                  placeholder="Nhập mức giảm giá"
                  thousandSeparator=""
                  validation={validation}
                  onBlur={validation.handleBlur}
                  showCurrency="%"
                  formatOptions={{ decimalScale: 0 }}
                />
              </Col>
            </Row>
            <p className={styles.CombineText}>
              <strong>Kết hợp được với:</strong>
              {' '}
              Kết hợp với nhóm khuyến mãi Phổ biến và Tài khoản
            </p>
          </div>
        </Card>

        <Card className={styles.Section}>
          <h3 className={styles.Title}>Ngày lưu trú</h3>

          <div className={styles.FormField}>
            <p className={styles.FormFieldTitle}>Thời gian lưu trú</p>
            <Row className={styles.DateRow}>
              <Col xs={5} className={styles.DateCol}>
                <StayDatesDatePicker
                  prefix="Bắt đầu vào"
                  placeholder="Chọn ngày bắt đầu"
                  value={validation.values.stayAt[0]?.startAt}
                  minDate={stayStartMinDate}
                  onChange={onChangeStayStartAt}
                />
                {showStayStartAtError && (
                  <FormFeedback
                    type="invalid"
                    className="d-block font-size-12 mt-2"
                  >
                    {stayStartAtError}
                  </FormFeedback>
                )}
              </Col>
              <span className={styles.Dash}>-</span>
              <Col xs={5} className={styles.DateCol}>
                <StayDatesDatePicker
                  prefix="Kết thúc vào"
                  placeholder="Chọn ngày kết thúc"
                  value={validation.values.stayAt[0]?.endAt}
                  minDate={stayEndMinDate}
                  onChange={onChangeStayEndAt}
                />
                {showStayEndAtError && (
                  <FormFeedback
                    type="invalid"
                    className="d-block font-size-12 mt-2"
                  >
                    {stayEndAtError}
                  </FormFeedback>
                )}
              </Col>
            </Row>
          </div>

          <div className={styles.SummaryPromotion}>
            <p className={styles.FormFieldTitle}>
              Giảm giá sẽ được áp dụng cho các lưu trú trong khoảng thời gian
              sau:
            </p>
            {validation.values.stayAt[0]?.startAt &&
              validation.values.stayAt[0]?.endAt && (
                <span className={styles.FormFieldSubTitle}>
                  {`${convertIsoDateToString(validation.values.stayAt[0].startAt, 'D [tháng] M yyyy')} – ${convertIsoDateToString(validation.values.stayAt[0].endAt, 'D [tháng] M yyyy')}`}
                </span>
            )}
          </div>
        </Card>

        <Card className={styles.Section}>
          <h3 className={styles.Title}>Tên khuyến mãi</h3>

          <div className={styles.FormField}>
            <div className={styles.FormFieldTitleWrapper}>
              <p className={styles.FormFieldTitle}>
                Quý vị muốn đặt tên khuyến mãi này là gì?
              </p>

              <span className={styles.FormFieldSubTitle}>
                Tên này chỉ dành cho Quý vị. Chúng tôi sẽ không hiển thị tên cho
                khách trên Bookese.com.
              </span>
            </div>

            <AppInputValidation
              type="text"
              id="name"
              name="name"
              validation={validation}
              placeholder="Nhập tên khuyến mãi"
            />
          </div>
        </Card>

        <div className={isExtranet ? styles.StickyButtonsExtranet : styles.StickyButtons}>
          <div className={styles.ButtonWrap}>
            <Link
              to={{
                pathname: LOCATIONS.LOCATION_DETAIL.PROMOTION,
                search: `?place=${placeId}`,
              }}
            >
              <Button color={themeColor.SECONDARY}>Hủy</Button>
            </Link>

            <Button
              onClick={validation.handleSubmit}
              disabled={promotionId && promotionDetail.isEditable === false}
            >
              {promotionId ? 'Cập nhật' : 'Tạo'}
            </Button>
          </div>
        </div>

        <ModalPromotionSummarize
          isOpen={isOpenModal}
          loading={isLoadingCreatePromotion}
          validation={validation}
          roomOfPlaceList={roomOfPlaceList}
          placePriceTypes={placePriceTypes}
          onCloseClick={onCloseModal}
          onSubmit={onSubmitCreatePromotion}
        />

        <ModalSuccess
          isOpen={isOpenSuccessModal}
          onCloseModal={handleCloseSuccessModal}
          message={promotionId ? 'Bạn đã cập nhật khuyến mãi thành công' : 'Bạn đã tạo khuyến mãi thành công'}
        />
      </div>
    </div>
  );
}

CreateRankPromotion.propTypes = {
  isLoadingCreatePromotion: PropTypes.bool.isRequired,

  roomOfPlaceList: PropTypes.instanceOf(Array),
  placePriceTypes: PropTypes.instanceOf(Array),
  promotionDetail: PropTypes.instanceOf(Object),
  placePromotionList: PropTypes.instanceOf(Array),

  actionGetListRoomsOfPlace: PropTypes.func.isRequired,
  actionGetPlacePriceTypes: PropTypes.func.isRequired,
  actionCreatePlacePromotion: PropTypes.func.isRequired,
  actionGetPromotionDetail: PropTypes.func.isRequired,
  actionClearDataPromotionDetail: PropTypes.func.isRequired,
  actionGetPlacePromotionList: PropTypes.func.isRequired,
};

CreateRankPromotion.defaultProps = {
  roomOfPlaceList: INITIAL_VALUE.ARRAY,
  placePriceTypes: INITIAL_VALUE.ARRAY,
  promotionDetail: INITIAL_VALUE.OBJECT,
  placePromotionList: INITIAL_VALUE.ARRAY,
};

export default CreateRankPromotion;
